import { lazy, Suspense, useEffect, useState, type JSX } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { api } from "../kit/api";
import { ToastProvider } from "../kit/overlays";
import { Boot, Skeleton } from "../kit/ui";
import { MeCtx, useCan, type Me } from "./context";
import { Guard } from "./Locked";
import { Layout } from "./Layout";
import { SignIn } from "../pages/SignIn";
import { Setup } from "../pages/Setup";

const Portfolio = lazy(() => import("../pages/Portfolio"));
const Tenants = lazy(() => import("../pages/Tenants"));
const TenantRoutes = lazy(() => import("../pages/tenant/TenantRoutes"));
const Alerts = lazy(() => import("../pages/Alerts"));
const Baselines = lazy(() => import("../pages/Baselines"));
const Reports = lazy(() => import("../pages/Reports"));
const Billing = lazy(() => import("../pages/Billing"));
const Team = lazy(() => import("../pages/Team"));
const Integrations = lazy(() => import("../pages/Integrations"));
const Branding = lazy(() => import("../pages/Branding"));
const Support = lazy(() => import("../pages/Support"));
const Notifications = lazy(() => import("../pages/Notifications"));
const Audit = lazy(() => import("../pages/Audit"));

/** Every page a role may not use opens as a locked state; the Worker refuses the same calls. */
function Pages() {
  const can = useCan();
  const tenants = (title: string, el: JSX.Element) => <Guard allowed={can.tenants} title={title} what="work in customer tenants">{el}</Guard>;
  return (
    <Routes>
      <Route index element={<Portfolio />} />
      <Route path="tenants" element={tenants("Tenants", <Tenants />)} />
      <Route path="t/:id/*" element={tenants("Tenant", <TenantRoutes />)} />
      <Route path="alerts" element={tenants("Alerts", <Alerts />)} />
      <Route path="baselines" element={tenants("Baselines", <Baselines />)} />
      <Route path="reports" element={<Guard allowed={can.reports} title="Customer reports" what="customer reports"><Reports /></Guard>} />
      <Route path="billing" element={<Guard allowed={can.business} title="Billing" what="billing and prices"><Billing /></Guard>} />
      <Route path="team" element={<Team />} />
      <Route path="integrations" element={<Guard allowed={can.manage} title="Integrations" what="the partner's integrations"><Integrations /></Guard>} />
      <Route path="branding" element={<Guard allowed={can.manage} title="Branding & sign-in" what="branding and sign-in settings"><Branding /></Guard>} />
      <Route path="notifications" element={<Notifications />} />
      <Route path="support" element={<Support />} />
      <Route path="audit" element={<Audit />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function Signed() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const load = () =>
    (setError(null), api<Me>("/api/me").then((m) => (m.member.must_change ? window.location.assign("/setup?change=1") : setMe(m))).catch((e) => (e as { status?: number }).status !== 401 && setError(e as Error)));
  useEffect(() => void load(), []);
  if (!me) return <Boot error={error} onRetry={load} />;
  return (
    <MeCtx.Provider value={me}>
      <Layout>
        <Suspense fallback={<div className="page"><Skeleton rows={6} /></div>}>
          <Pages />
        </Suspense>
      </Layout>
    </MeCtx.Provider>
  );
}

export function App() {
  return (
    <ToastProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/signin" element={<SignIn />} />
          <Route path="/setup" element={<Setup />} />
          <Route path="/*" element={<Signed />} />
        </Routes>
      </BrowserRouter>
    </ToastProvider>
  );
}
